import { useCallback, useMemo, useState } from "react"
import { useRefreshPassport } from "@mezo-org/passport"
import { PayloadAction } from "@reduxjs/toolkit"

import {
  depositCollateral,
  selectMinimumCollateralRatioInCurrentMode,
  selectUpdateCollateralAmount,
  selectUpdateCollateralRatio,
  selectUpdateCollateralStrategy,
  selectUserLoan,
  selectWalletBalances,
  setUpdateCollateralAmount,
  setUpdateCollateralRatio,
  useDappDispatch,
  useDappSelector,
  withdrawCollateral,
} from "#/redux-state"
import {
  BorrowCollateralChangePosthogEventProps,
  BorrowCollateralManagementActionUnit,
  BorrowCollateralManagementStrategy,
} from "#/shared/types"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  normalizeDecimalNumberInput,
  trimDecimals,
} from "#/shared/utils"

import { usePosthogCollateralManagementEvent } from "../posthog-events"
import { useSegmentCollateralManagementEvent } from "../segment-events"

import {
  GAS_BUFFER_FOR_BORROW_COLLATERAL_TRANSACTION,
  useCalculateCollateralAmountBasedOnRatio,
  useCalculateCollateralizationRatio,
  useCollateralizationRatioForExistingLoan,
  useMinimumRequiredCollateral,
} from "./borrow-calculation-helpers"
import { useFetchAllBorrowData } from "./borrow-system"

export const useManageCollateralAmount = (
  activeSetupAction: BorrowCollateralManagementActionUnit,
) => {
  const dispatch = useDappDispatch()

  const userLoan = useDappSelector(selectUserLoan)

  const updateCollateralStrategy = useDappSelector(
    selectUpdateCollateralStrategy,
  )

  const updateCollateralAmount = useDappSelector(selectUpdateCollateralAmount)
  const updateCollateralRatio = useDappSelector(selectUpdateCollateralRatio)

  const { calculateCollateralizationRatio } =
    useCalculateCollateralizationRatio()

  const { calculateCollateralAmountBasedOnRatio } =
    useCalculateCollateralAmountBasedOnRatio()

  const { mBTC } = useDappSelector(selectWalletBalances)

  const debtAmount = userLoan?.debtAmount ?? 0n
  const collateralAmount = userLoan?.collateralAmount ?? 0n
  const mBtcDecimals = Number(mBTC.decimals)

  const { collateralizationRatio: prevCollateralizationRatio } =
    useCollateralizationRatioForExistingLoan(
      debtAmount,
      0n,
      collateralAmount,
      0n,
    )

  const { requiredCollateralAmount: minimumCollateralAmount } =
    useMinimumRequiredCollateral(debtAmount)

  const availableFundsBalance = useMemo(() => {
    if (
      updateCollateralStrategy ===
      BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
    )
      return mBTC.balance - GAS_BUFFER_FOR_BORROW_COLLATERAL_TRANSACTION

    return userLoan ? userLoan.collateralAmount - minimumCollateralAmount : 0n
  }, [
    updateCollateralStrategy,
    mBTC.balance,
    userLoan,
    minimumCollateralAmount,
  ])

  const availableFundsBalanceLabel = useMemo(
    () => bigIntToHumanReadableFormat(availableFundsBalance, mBtcDecimals, 6),
    [availableFundsBalance, mBtcDecimals],
  )

  const onCollateralAmountChange = useCallback(
    (inputAmount: string, action = activeSetupAction) => {
      const normalizedInput = normalizeDecimalNumberInput(inputAmount)

      const trimmedInputAmount = trimDecimals(normalizedInput, 18)

      if (action === BorrowCollateralManagementActionUnit.AMOUNT) {
        const prevCollateralAmount = collateralAmount
        const changeCollateralAmount = floatToBigInt(trimmedInputAmount)

        const newCollateralAmount =
          updateCollateralStrategy ===
          BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
            ? prevCollateralAmount + changeCollateralAmount
            : prevCollateralAmount - changeCollateralAmount

        const newCollateralizationRatio = calculateCollateralizationRatio(
          debtAmount,
          newCollateralAmount,
        )

        dispatch(setUpdateCollateralAmount(trimmedInputAmount))
        dispatch(
          setUpdateCollateralRatio(
            bigIntToFloat(
              newCollateralizationRatio - prevCollateralizationRatio,
              16,
              2,
            ),
          ),
        )
      } else {
        const newCollateralizationRatio = floatToBigInt(trimmedInputAmount, 16)

        const newCollateralAmount = calculateCollateralAmountBasedOnRatio(
          debtAmount,
          newCollateralizationRatio,
        )

        dispatch(
          setUpdateCollateralAmount(
            bigIntToFloat(newCollateralAmount, mBtcDecimals, mBtcDecimals),
          ),
        )
        dispatch(setUpdateCollateralRatio(trimmedInputAmount))
      }
    },
    [
      activeSetupAction,
      calculateCollateralAmountBasedOnRatio,
      calculateCollateralizationRatio,
      collateralAmount,
      debtAmount,
      dispatch,
      mBtcDecimals,
      prevCollateralizationRatio,
      updateCollateralStrategy,
    ],
  )

  const onMaxAmountClick = useCallback(() => {
    onCollateralAmountChange(
      bigIntToFloat(availableFundsBalance, mBtcDecimals, 8),
      BorrowCollateralManagementActionUnit.AMOUNT,
    )
  }, [availableFundsBalance, mBtcDecimals, onCollateralAmountChange])

  const prevCollateralAmountLabel = useMemo(
    () =>
      userLoan?.collateralAmount
        ? bigIntToHumanReadableFormat(userLoan.collateralAmount, 18, 6)
        : "0",
    [userLoan?.collateralAmount],
  )

  const newCollateralAmount = useMemo(() => {
    const updateAmount =
      updateCollateralStrategy ===
      BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
        ? floatToBigInt(updateCollateralAmount)
        : -floatToBigInt(updateCollateralAmount)

    return collateralAmount + updateAmount
  }, [collateralAmount, updateCollateralAmount, updateCollateralStrategy])

  const newCollateralAmountLabel = useMemo(
    () => bigIntToHumanReadableFormat(newCollateralAmount, mBtcDecimals, 6),
    [newCollateralAmount, mBtcDecimals],
  )

  const isValidCollateral = useMemo(() => {
    if (!updateCollateralAmount) return false

    const parsedUpdateCollateralAmount = floatToBigInt(updateCollateralAmount)

    return (
      parsedUpdateCollateralAmount <= availableFundsBalance &&
      parsedUpdateCollateralAmount > 0n
    )
  }, [updateCollateralAmount, availableFundsBalance])

  return {
    updateCollateralStrategy,
    // Input values
    updateCollateralAmount,
    updateCollateralRatio,
    // On input change
    onCollateralAmountChange,
    onMaxAmountClick,
    isValidCollateral,

    // Available funds for either deposit or withdraw
    availableFundsBalance,
    availableFundsBalanceLabel,

    // Collateral change
    prevCollateralAmount: userLoan?.collateralAmount ?? 0n,
    prevCollateralAmountLabel,
    newCollateralAmount,
    newCollateralAmountLabel,
  }
}

export const useManageCollateralRatio = () => {
  const userLoan = useDappSelector(selectUserLoan)

  const minimumCollateralRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const updateCollateralStrategy = useDappSelector(
    selectUpdateCollateralStrategy,
  )

  const updateCollateralAmount = useDappSelector(selectUpdateCollateralAmount)

  const debtAmount = userLoan?.debtAmount ?? 0n
  const collateralAmount = userLoan?.collateralAmount ?? 0n

  const collateralAmountChange =
    updateCollateralStrategy ===
    BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
      ? floatToBigInt(updateCollateralAmount)
      : floatToBigInt(-updateCollateralAmount)

  const {
    collateralizationRatio: prevCollateralizationRatio,
    collateralizationRatioLabel: prevCollateralizationRatioLabel,
  } = useCollateralizationRatioForExistingLoan(
    debtAmount,
    0n,
    collateralAmount,
    0n,
  )

  const {
    collateralizationRatio: newCollateralizationRatio,
    collateralizationRatioLabel: newCollateralizationRatioLabel,
  } = useCollateralizationRatioForExistingLoan(
    debtAmount,
    0n,
    collateralAmount,
    collateralAmountChange,
  )

  const collateralizationRatioChange = useMemo(
    () =>
      newCollateralizationRatio && prevCollateralizationRatio
        ? newCollateralizationRatio - prevCollateralizationRatio
        : 0n,
    [newCollateralizationRatio, prevCollateralizationRatio],
  )

  const collateralizationRatioChangeLabel = useMemo(
    () =>
      bigIntToHumanReadableFormat(
        BigInt(Number(collateralizationRatioChange)),
        16,
        2,
      ),
    [collateralizationRatioChange],
  )

  const isValidRatio = useMemo(() => {
    if (!newCollateralizationRatio) return false
    return newCollateralizationRatio >= (minimumCollateralRatio ?? 0n)
  }, [newCollateralizationRatio, minimumCollateralRatio])

  return {
    isValidRatio,
    prevCollateralizationRatio,
    prevCollateralizationRatioLabel,
    newCollateralizationRatio,
    newCollateralizationRatioLabel,
    collateralizationRatioChange,
    collateralizationRatioChangeLabel,
  }
}

export const useManageCollateralTransaction = () => {
  const dispatch = useDappDispatch()

  const { refreshTokensBalances } = useRefreshPassport()

  const userLoan = useDappSelector(selectUserLoan)

  const updateCollateralStrategy = useDappSelector(
    selectUpdateCollateralStrategy,
  )

  const updateCollateralAmount = useDappSelector(selectUpdateCollateralAmount)

  const { prevCollateralizationRatio, newCollateralizationRatio } =
    useManageCollateralRatio()

  const {
    emitCollateralDepositSegmentEvent,
    emitCollateralWithdrawSegmentEvent,
  } = useSegmentCollateralManagementEvent()

  const {
    emitCollateralDepositStartedEvent,
    emitCollateralDepositErrorEvent,
    emitCollateralDepositEvent,
    emitCollateralWithdrawStartedEvent,
    emitCollateralWithdrawErrorEvent,
    emitCollateralWithdrawEvent,
  } = usePosthogCollateralManagementEvent()

  const { fetchAllBorrowData } = useFetchAllBorrowData()

  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const updateCollateralHandler = useCallback(async () => {
    const prevCollateralAmount = +bigIntToFloat(
      userLoan?.collateralAmount ?? 0n,
      18,
      8,
    )

    const newCollateralAmount =
      updateCollateralStrategy ===
      BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
        ? prevCollateralAmount + +updateCollateralAmount
        : prevCollateralAmount - +updateCollateralAmount

    const posthogEventProps: BorrowCollateralChangePosthogEventProps = {
      collateral: newCollateralAmount,
      prev_collateral: prevCollateralAmount,
      collateralization_ratio: +bigIntToFloat(newCollateralizationRatio, 16, 2),
      prev_collateralization_ratio: +bigIntToFloat(
        prevCollateralizationRatio,
        16,
        2,
      ),
    }

    setIsLoading(true)
    setHasError(false)

    let success = false
    const normalizedCollateralAmount = floatToBigInt(updateCollateralAmount)

    if (
      updateCollateralStrategy ===
      BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL
    ) {
      emitCollateralDepositStartedEvent()

      const result = (await dispatch(
        depositCollateral(normalizedCollateralAmount),
      )) as PayloadAction<boolean>

      success = result.payload

      if (!success) {
        emitCollateralDepositErrorEvent()
      } else {
        emitCollateralDepositEvent(posthogEventProps)
        emitCollateralDepositSegmentEvent(posthogEventProps)
      }
    } else {
      emitCollateralWithdrawStartedEvent()

      const result = (await dispatch(
        withdrawCollateral(normalizedCollateralAmount),
      )) as PayloadAction<boolean>

      success = result.payload

      if (!success) {
        emitCollateralWithdrawErrorEvent()
      } else {
        emitCollateralWithdrawEvent(posthogEventProps)
        emitCollateralWithdrawSegmentEvent(posthogEventProps)
      }
    }

    if (success) {
      await fetchAllBorrowData({ refetch: true })
      await refreshTokensBalances()
    }

    setIsLoading(false)
    setHasError(!success)
  }, [
    userLoan?.collateralAmount,
    updateCollateralStrategy,
    updateCollateralAmount,
    newCollateralizationRatio,
    prevCollateralizationRatio,
    emitCollateralDepositStartedEvent,
    dispatch,
    emitCollateralDepositErrorEvent,
    emitCollateralDepositEvent,
    emitCollateralWithdrawStartedEvent,
    emitCollateralWithdrawErrorEvent,
    emitCollateralWithdrawEvent,
    emitCollateralDepositSegmentEvent,
    emitCollateralWithdrawSegmentEvent,
    fetchAllBorrowData,
    refreshTokensBalances,
  ])

  return {
    isLoading,
    hasError,
    updateCollateralHandler,
  }
}
